import React, { useEffect, useRef, useState } from "react";
import { Brain, Pencil, Check } from "lucide-react";

/**
 * CallContextCard — one block of call context, sitting in the Copilot thread.
 *
 * Every "send to Copilot" from the call panel drops one of these in. It is
 * context, not a question: the agent is told about it and does nothing until
 * the engineer actually types something. That's why it renders as a distinct
 * block rather than a user bubble — a user bubble implies a turn was taken,
 * and none was.
 *
 * Editable in place, because live transcription mangles exactly the words that
 * matter (product names, versions, error codes) and the engineer is the one
 * who heard the call. Saving replaces what the agent will see; the earlier
 * wording is gone rather than kept as a correction, since nothing has read it
 * yet.
 *
 * `superseded` marks a block that was folded into a rewrite of the whole
 * context (GlobalContextPanel's "Full context" save). It stays in the thread
 * because it is a record of what the agent was told at that point, but it is no
 * longer part of the context and so is no longer editable — editing it would
 * write into a block the server has already forgotten.
 */
const CallContextCard = ({ text, onSave, superseded = false }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || "");
  const textareaRef = useRef(null);

  // Follow the prop only while NOT editing, so a re-render can't overwrite
  // what the engineer is typing.
  useEffect(() => { if (!editing) setDraft(text || ""); }, [text, editing]);

  useEffect(() => {
    if (!editing) return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 320) + "px";
    ta.focus();
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== (text || "").trim()) onSave?.(next);
    else setDraft(text || "");
  };

  return (
    <div className={`d5-call-context ${superseded ? "d5-call-context-superseded" : ""}`}>
      <div className="d5-call-context-head">
        <span className="d5-call-context-title">
          <Brain size={11} /> Call / Chat context
          {superseded && <span className="d5-call-context-tag">folded into the edited context</span>}
        </span>
        {!superseded && (
          <button
            type="button"
            className="d5-call-context-btn"
            title={editing ? "Save context" : "Edit context"}
            onClick={() => (editing ? commit() : setEditing(true))}
          >
            {editing ? <Check size={13} /> : <Pencil size={12} />}
          </button>
        )}
      </div>

      {editing ? (
        <textarea
          ref={textareaRef}
          className="d5-call-context-edit"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            const ta = textareaRef.current;
            if (ta) { ta.style.height = "auto"; ta.style.height = Math.min(ta.scrollHeight, 320) + "px"; }
          }}
          // Enter commits, Shift+Enter is a newline — same idiom as the composers.
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setDraft(text || ""); setEditing(false); }
          }}
        />
      ) : (
        <div className="d5-call-context-body">{text}</div>
      )}
    </div>
  );
};

export default CallContextCard;
